import { AddAlt, CheckmarkFilled } from '@carbon/icons-react';
import { Pad, Separator, VertBox, WindowTitle } from 'component/basics';
import { CTAButton } from 'component/button';
import { colorSurfaceSecondary, colorTextOnPrimaryButton, colorTextSecondary } from 'component/color';
import { AdminScreen } from 'component/screen';
import { Heading, UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { getDefaultFeatureConfig } from 'feature/feature-helpers';
import { useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { Datastore, useDatastore, useGlobalProperty, useStructureKey } from 'util/datastore';
import { useMergedDefaultFeatures, useStoredDefaultFeatures } from 'util/features';
import { featuresProperty, type Structure, type TLabel, t } from 'util/stdtypes';
import { getDeploymentConfig, makeAssetUrl } from 'util/util';

export const SETTINGS_CAPABILITY = 'admin/settings';

export type SettingsConfig = {};

export const SettingsStructure: Structure<SettingsConfig> = {
    key: 'settings',
    isAdminUi: true,
    name: 'Settings',
    screen: SettingsScreen,
};

type BaseFeatureSetting = {
    label: TLabel;
    descriptionLabel?: TLabel;
    imageUrl?: string;
    buttonLabel: TLabel;
    enabledButtonLabel: TLabel;
    settingType: 'instance' | 'global';
};

type InstanceFeatureSetting = BaseFeatureSetting & {
    structureKey: string;
    featureKey: string;
    settingType: 'instance';
};

type GlobalFeatureSetting = BaseFeatureSetting & {
    features: {
        structure: string;
        feature: string;
    }[];
    settingType: 'global';
};

export function SettingsScreen(): JSX.Element {
    const deploymentConfig = getDeploymentConfig();
    const headingText = useTranslation('{psiBrandName} Settings', { psiBrandName: deploymentConfig.psiBrandName ?? 'PSI' });
    return (
        <AdminScreen capability={SETTINGS_CAPABILITY}>
            <WindowTitle text={headingText} />
            <AdminScreen.Header>
                <Heading text={headingText} type='2xl' weight='medium' />
            </AdminScreen.Header>
            <AdminScreen.Body>
                <Pad size={40} />
                <VertBox gap={40} wide>
                    <ProsocialFormatsSettings />
                    <Separator />
                    <LabsSettings />
                    <Separator />
                    <ModerationSettings />
                    <Separator />
                    <AdditionalFeaturesSettings />
                </VertBox>
                <Pad size={80} />
            </AdminScreen.Body>
        </AdminScreen>
    );
}

function ProsocialFormatsSettings(): JSX.Element {
    const baseSetting = {
        buttonLabel: t('Add format'),
        enabledButtonLabel: t('Added'),
        settingType: 'instance' as const,
    };
    const settings = [
        {
            ...baseSetting,
            label: t('Comment Slider'),
            descriptionLabel: t('Ask users to respond to your question along a spectrum (e.g. “Strongly no” to “Strongly yes”).'),
            imageUrl: 'images/comment-slider.svg',
            structureKey: 'editorialdash',
            featureKey: 'comment-slider-template',
        },
        {
            ...baseSetting,
            label: t('Representing Perspectives'),
            descriptionLabel: t('Ask users from a specific set of perspectives to respond to your question.'),
            imageUrl: 'images/representing-perspectives.svg',
            structureKey: 'editorialdash',
            featureKey: 'representing-perspectives-template',
        },
        {
            ...baseSetting,
            label: t('Dialogue'),
            descriptionLabel: t('Ask users to respond to your question with a simple “yes” or “no” answer.'),
            imageUrl: 'images/dialogue.svg',
            structureKey: 'editorialdash',
            featureKey: 'dialogue-template',
        },
        {
            ...baseSetting,
            label: t('Audience Input'),
            descriptionLabel: t('Collect questions, testimonials, and stories from users. This allows for a greater range of engagement.'),
            imageUrl: 'images/audience-input.svg',
            structureKey: 'editorialdash',
            featureKey: 'audience-input-template',
        },
    ];
    return (
        <SettingsSection
            descriptionLabel={t('Every discussion space is structured by a prosocial guided format.')}
            headingLabel={t('Prosocial Formats')}
            settings={settings}
        />
    );
}

function LabsSettings(): JSX.Element {
    const baseSetting = {
        buttonLabel: t('Add feature'),
        enabledButtonLabel: t('Added'),
        settingType: 'instance' as const,
    };
    const settings = [
        {
            ...baseSetting,
            label: t('Conversation Helper'),
            descriptionLabel: t('Give public guidance and feedback on conversations with the help of an AI chatbot.'),
            structureKey: 'editorialdash',
            featureKey: 'conversation-helper-template',
        },
        {
            ...baseSetting,
            label: t('Video Voting'),
            descriptionLabel: t('Overlay questions on video content.'),
            structureKey: 'admin',
            featureKey: 'videovotinglist',
        },
    ];
    return (
        <SettingsSection
            descriptionLabel={t(
                'Prosocial formats in earlier stages of development, available for experimentation. Labs features are fully functional, but lack standardized defaults and may change over time.',
            )}
            headingLabel={t('Labs')}
            settings={settings}
        />
    );
}

function ModerationSettings(): JSX.Element {
    const baseSetting = {
        buttonLabel: t('Add feature'),
        enabledButtonLabel: t('Added'),
        settingType: 'global' as const,
    };
    const settings = [
        {
            ...baseSetting,
            label: t('Pre-Moderation'),
            descriptionLabel: t('Use AI to ensure user-submitted posts comply with Community Guidelines before publishing.'),
            features: [
                {
                    structure: 'question',
                    feature: 'premoderation',
                },
                {
                    structure: 'question',
                    feature: 'aimoderation',
                },
                {
                    structure: 'question',
                    feature: 'jigsawmoderation',
                },
            ],
        },
        {
            ...baseSetting,
            label: t('Blocklist'),
            descriptionLabel: t('Block specific words from all user-submitted posts.'),
            features: [
                {
                    structure: 'question',
                    feature: 'blocklist',
                },
            ],
        },
    ];
    return <SettingsSection headingLabel={t('Moderation')} settings={settings} />;
}

function AdditionalFeaturesSettings(): JSX.Element {
    const baseSetting = {
        buttonLabel: t('Add feature'),
        enabledButtonLabel: t('Added'),
        settingType: 'global' as const,
    };
    const settings = [
        {
            ...baseSetting,
            label: t('Titled Writers'),
            descriptionLabel: t(
                'Identify select users as verified members of your organization when writing comments or responding to others.',
            ),
            features: [
                {
                    structure: 'question',
                    feature: 'titledWriter',
                },
                {
                    structure: 'profile',
                    feature: 'profiletitledwritersetting',
                },
            ],
        },
        {
            ...baseSetting,
            label: t('Channels'),
            descriptionLabel: t(
                'Organize spaces into distinct channels, to allow editorial and moderation teams to focus on relevant content.',
            ),
            features: [
                {
                    structure: 'moddashboard',
                    feature: 'channelbase',
                },
                {
                    structure: 'moddashboard',
                    feature: 'channelselectionmoddashboard',
                },
                {
                    structure: 'moddashboard',
                    feature: 'moddashboardsettings',
                },
                {
                    structure: 'moddashboard',
                    feature: 'channelsettings',
                },
                {
                    structure: 'editorialdash',
                    feature: 'editorialdashchannelselection',
                },
                {
                    structure: 'question',
                    feature: 'editorialdashchannelselection',
                },
            ],
        },
    ];
    return <SettingsSection headingLabel={t('Additional Features')} settings={settings} />;
}

type SettingsSectionProps = {
    headingLabel: TLabel;
    descriptionLabel?: TLabel;
    settings: (InstanceFeatureSetting | GlobalFeatureSetting)[];
};

function SettingsSection({ headingLabel, descriptionLabel, settings }: SettingsSectionProps): JSX.Element {
    const s = SettingsSectionStyle;
    return (
        <View>
            <Heading label={headingLabel} type='large' weight='medium' />
            {descriptionLabel && (
                <>
                    <Pad size={12} />
                    <UtilityText color={colorTextSecondary} label={descriptionLabel} type='small' />
                </>
            )}
            <Pad size={24} />
            <View style={s.settingsContainer}>
                {settings.map((setting, index) => (
                    <SettingsSectionItem key={index} {...setting} />
                ))}
            </View>
        </View>
    );
}

const SettingsSectionStyle = StyleSheet.create({
    settingsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
    },
});

function SettingsSectionItem(setting: InstanceFeatureSetting | GlobalFeatureSetting): JSX.Element {
    const s = SettingsSectionItemStyle;
    const { label, descriptionLabel, imageUrl, settingType } = setting;
    return (
        <View style={s.container}>
            {imageUrl && (
                <>
                    <Image source={{ uri: makeAssetUrl(imageUrl) }} style={s.image} />
                    <Pad size={24} />
                </>
            )}
            <Heading label={label} type='small' weight='medium' />
            <Pad size={12} />
            <UtilityText color={colorTextSecondary} label={descriptionLabel} type='small' />
            <Pad size={20} />
            <View style={s.buttonContainer}>
                {settingType === 'instance' && <InstanceSettingButtonContainer {...setting} />}
                {settingType === 'global' && <GlobalSettingButtonContainer {...setting} />}
            </View>
        </View>
    );
}

const SettingsSectionItemStyle = StyleSheet.create({
    container: {
        width: 305,
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    image: {
        width: 187,
        height: 130,
        alignSelf: 'center',
    },
    buttonContainer: {
        marginTop: 'auto',
    },
});

function InstanceSettingButtonContainer({
    structureKey,
    featureKey,
    buttonLabel,
    enabledButtonLabel,
}: InstanceFeatureSetting): JSX.Element {
    const datastore = useDatastore();

    const instanceKey = 'one';

    return (
        <Datastore
            config={datastore.getConfig()}
            globalParams={datastore.props.globalParams}
            instanceKey={instanceKey}
            isEmbedded={true}
            isLive={datastore.props.isLive}
            language={datastore.props.language}
            personaPreview={datastore.props.personaPreview}
            readOnly={datastore.props.readOnly}
            siloKey={datastore.props.siloKey}
            structureKey={structureKey}
            testState={datastore.props.testState}
        >
            <SettingsSectionAddToButton buttonLabel={buttonLabel} enabledButtonLabel={enabledButtonLabel} featureKey={featureKey} />
        </Datastore>
    );
}

type SettingsSectionAddToButtonProps = {
    featureKey: string;
    buttonLabel: TLabel;
    enabledButtonLabel: TLabel;
};

function SettingsSectionAddToButton({ featureKey, buttonLabel, enabledButtonLabel }: SettingsSectionAddToButtonProps): JSX.Element {
    const structureKey = useStructureKey();
    const mergedDefaultFeatures = useMergedDefaultFeatures(structureKey);
    const defaultState = mergedDefaultFeatures[featureKey] ?? false;

    const features = useGlobalProperty(featuresProperty);
    const enabled = features?.[featureKey] ?? defaultState;
    const datastore = useDatastore();
    return (
        <CTAButton
            icon={enabled ? <CheckmarkFilled color={colorTextOnPrimaryButton} /> : <AddAlt />}
            label={enabled ? enabledButtonLabel : buttonLabel}
            onPress={() => {
                const newFeatures = {
                    ...(features ?? {}),
                    [featureKey]: !enabled,
                };
                datastore.setGlobalProperty(featuresProperty, newFeatures);
            }}
            type={enabled ? 'primary' : 'secondary'}
        />
    );
}

function GlobalSettingButtonContainer({ features, buttonLabel, enabledButtonLabel }: GlobalFeatureSetting): JSX.Element {
    const datastore = useDatastore();
    const [saving, setSaving] = useState(false);
    // Can't use useMergedDefaultFeatures because we need to
    // inspect multiple structures.
    const storedDefaultFeatures = useStoredDefaultFeatures();
    const allMergedDefaultFeatures = {};
    for (const feature of features) {
        allMergedDefaultFeatures[feature.structure] = {
            ...(getDefaultFeatureConfig()[feature.structure] ?? {}),
            ...(storedDefaultFeatures?.[feature.structure] ?? {}),
        };
    }
    const enabled = features.every((feature) => {
        const mergedDefaults = allMergedDefaultFeatures?.[feature.structure] ?? {};
        return Boolean(mergedDefaults[feature.feature]);
    });
    return (
        <CTAButton
            disabled={saving}
            icon={enabled ? <CheckmarkFilled color={colorTextOnPrimaryButton} /> : <AddAlt />}
            label={enabled ? enabledButtonLabel : buttonLabel}
            onPress={async () => {
                setSaving(true);
                for (const feature of features) {
                    await datastore.callServerAsync('global', 'setFeatureDefaults', {
                        structureKey: feature.structure,
                        featureKey: feature.feature,
                        value: !enabled,
                    });
                }
                setSaving(false);
            }}
            type={enabled ? 'primary' : 'secondary'}
        />
    );
}
